const DANGEROUS_SPREADSHEET_PREFIX = /^[\t\r\n ]*[=+\-@]/;

/**
 * RFC 4180 cell escaping plus spreadsheet-formula neutralization.
 * Contribution fields and original filenames are user-controlled. Prefixing
 * formula-looking values with an apostrophe keeps Excel/Sheets from executing
 * them while preserving readable content.
 */
export const csvEscape = (value) => {
  if (value === null || value === undefined) return "";
  let str = String(value);
  if (DANGEROUS_SPREADSHEET_PREFIX.test(str)) str = `'${str}`;
  if (/[",\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
};

const mediaNamesForRow = (row) =>
  Array.isArray(row?.media)
    ? row.media.map((filename) => String(filename || "").trim()).filter(Boolean)
    : [];

/**
 * Build the client-requested moderation export.
 *
 * Media Count + Media Files provide the accepted stable filename reference.
 * File paths/URLs are intentionally excluded from this administrative export;
 * thumbnails/links remain a separately scoped privacy and format decision.
 */
export const buildPostsCsv = ({
  posts,
  getLocationName,
  getUserName,
  getUserEmail,
  visibilityLabels,
}) => {
  const safePosts = Array.isArray(posts) ? posts : [];
  const fieldNameSet = new Set();
  safePosts.forEach((row) => {
    (row?.fields || []).forEach((field) => {
      if (field?.field_name && field.field_type !== "file") {
        fieldNameSet.add(field.field_name);
      }
    });
  });
  const dynamicFieldNames = Array.from(fieldNameSet);
  const headers = [
    "ID",
    "Location",
    "User Name",
    "Email",
    "Year",
    "Visibility",
    "Approval Status",
    "Reliable",
    "Shape Type",
    "Project",
    "Created At",
    "Last Updated",
    "Coordinates",
    "Media Count",
    "Media Files",
    ...dynamicFieldNames,
  ];

  const lines = [headers.map(csvEscape).join(",")];
  safePosts.forEach((row) => {
    const fieldByName = {};
    (row?.fields || []).forEach((field) => {
      if (field?.field_name) fieldByName[field.field_name] = field.field_value;
    });
    const mediaNames = mediaNamesForRow(row);
    const values = [
      row?.id ?? "",
      getLocationName(row),
      getUserName(row),
      getUserEmail(row),
      row?.year ?? "",
      visibilityLabels[row?.visibility] || row?.visibility || "",
      row?.approval_status || "",
      row?.is_reliable ? "Yes" : "No",
      row?.shape_type || "",
      row?.project_name || "",
      row?.created_at || "",
      row?.modified_at || row?.updated_at || row?.created_at || "",
      typeof row?.loc_id === "string"
        ? row.loc_id
        : JSON.stringify(row?.loc_id ?? ""),
      mediaNames.length,
      mediaNames.join(" | "),
      ...dynamicFieldNames.map((name) => fieldByName[name] ?? ""),
    ];
    lines.push(values.map(csvEscape).join(","));
  });

  return {
    csv: `\uFEFF${lines.join("\r\n")}`,
    headers,
    rowCount: safePosts.length,
  };
};
